/**
 * `/teacher/dashboard` — landing page for the teacher shell.
 *
 * Shows three side-by-side counters (courses, courseware drafts, grading
 * drafts) and links into the relevant sub-routes. Loads everything in
 * parallel so the slowest list dictates the loading state, not the
 * fastest.
 */

import { Link } from 'react-router-dom';
import { useApiClient } from '../../api/ApiClientProvider';
import { useFetch } from '../../api/useFetch';
import { useSession } from '../../auth/SessionProvider';
import { LoadingState, ErrorState } from '../../components/States';

export function Dashboard() {
  const client = useApiClient();
  const { session } = useSession();
  const token = session?.token ?? '';

  const courses = useFetch(() => client.listCourses(token), [client, token]);
  const courseware = useFetch(
    () => client.listCoursewareDrafts(token),
    [client, token],
  );
  const grading = useFetch(
    () => client.listGradingDrafts(token),
    [client, token],
  );

  if (
    courses.status === 'loading' ||
    courseware.status === 'loading' ||
    grading.status === 'loading'
  ) {
    return <LoadingState label="Loading dashboard…" />;
  }

  if (courses.status === 'error') {
    return <ErrorState error={courses.error} />;
  }
  if (courseware.status === 'error') {
    return <ErrorState error={courseware.error} />;
  }
  if (grading.status === 'error') {
    return <ErrorState error={grading.error} />;
  }

  const reviewQueue = grading.data.filter(
    (g) => g.status === 'needs_review',
  ).length;
  const draftQueue = courseware.data.filter(
    (c) => c.status === 'needs_review',
  ).length;

  return (
    <section className="page" data-testid="teacher-dashboard">
      <header className="page__header">
        <h1>Dashboard</h1>
        <p className="muted">
          Quick overview of mock courses, AI-drafted courseware, and grading
          drafts that still need teacher review.
        </p>
      </header>

      <div className="cards">
        <article className="card card--stat">
          <h2>Courses</h2>
          <p className="card__stat" data-testid="stat-courses">
            {courses.data.length}
          </p>
          <p className="muted">Active mock courses for this term.</p>
          <Link to="/teacher/courses" className="card__link">
            View courses →
          </Link>
        </article>

        <article className="card card--stat">
          <h2>Courseware drafts</h2>
          <p className="card__stat" data-testid="stat-courseware">
            {draftQueue}
          </p>
          <p className="muted">AI drafts awaiting teacher review.</p>
          <Link to="/teacher/courseware" className="card__link">
            Review drafts →
          </Link>
        </article>

        <article className="card card--stat">
          <h2>Grading queue</h2>
          <p className="card__stat" data-testid="stat-grading">
            {reviewQueue}
          </p>
          <p className="muted">
            Submissions with AI-draft feedback that need teacher review before
            anything is shared.
          </p>
          <Link to="/teacher/grading" className="card__link">
            Open grading →
          </Link>
        </article>
      </div>
    </section>
  );
}
